import React, { useCallback, useEffect, useRef, useState } from 'react';
import Editor, { Quill } from 'react-quill';
import './CustomToolbarSVG';
import {
  ToolbarButton,
  ToolbarContainer,
  ToolbarSelect,
} from '../QuillToolbarStyles';

import styled from 'styled-components';
import ToolbarSec1 from './Section/ToolbarSec1';
import ToolbarSec2 from './Section/ToolbarSec2';
import ToolbarSec3 from './Section/ToolbarSec3';
import { dropdownDirMap } from '@components/wrapper/DropdownWrapper';
import { useQuillInstance } from './useQuillInstance';
import EditorColorPicker from './Section/EditorColorPicker';
import EditorBgColorPicker from './Section/EditorBgColorPicker';
import { scrollbarHide } from '@styles/variants/scrollbar';

const ToolbarWrap = styled.div<{
  transparent?: boolean;
}>`
  border-radius: var(--cardBorderRadius);
  background-color: ${({ transparent }) =>
    transparent ? 'transparent' : 'var(--cardBackgroundColor)'};
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
`;

type Props = {
  quillRef: React.RefObject<Editor>;
  setToolbar?: IsetToolbar;
  id: string;
  handleEmojiClick?: any;
  handleColorClick?: any;
  handleTableClick?: any;
  dir?: 'flex-start' | 'center' | 'flex-end';
  setPickerFocused?: React.Dispatch<React.SetStateAction<boolean>>;
  isHtmlMode?: boolean;
  setIsHtmlMode?: any;
  onToggleHtmlMode?: () => void; // HTML 모드 토글 함수 추가
  handleResetEditor?: () => void; // 문서 되돌리기 함수 추가
  emojiDirection?: keyof typeof dropdownDirMap | undefined;
  toolbarSide?: any;
  noToolbar?: boolean;
  style?: React.CSSProperties;
  transparent?: boolean;
  rows?: 1 | 2; // 1단(기본) / 2단
  collectionPath?: string;
  setVideoURLList?: (updater: (prev: string[]) => string[]) => void;
  videoDropdownDirection?: 'up' | 'down';
};

export interface IsetToolbar {
  size?: boolean;
  align?: boolean;
  color?: boolean;
  list?: boolean;
  blockquote?: boolean;
  codeblock?: boolean;
  link?: boolean;
  image?: boolean;
  file?: boolean;
  html?: boolean;
  emoji?: boolean;
  video?: boolean;
  header?: boolean;
  table?: boolean;
  divider?: boolean;
  indent?: boolean;
}

function CustomToolBar({
  quillRef,
  setToolbar,
  id,
  dir = 'flex-start',
  style,
  handleEmojiClick,
  isHtmlMode = false,
  onToggleHtmlMode,
  setIsHtmlMode,
  emojiDirection,
  toolbarSide,
  noToolbar = false,
  transparent = true,
  rows = 1,
  collectionPath,
  setVideoURLList,
  videoDropdownDirection = 'down',
}: Props) {
  const { align, size, color, list, header, html } = setToolbar || {};
  const quill = useQuillInstance(quillRef);
  const [isPickerVisible, setPickerVisible] = useState(false);
  const [isBgPickerVisible, setBgPickerVisible] = useState(false);

  const resetPickerDisplay = useCallback(() => {
    try {
      const container = document.getElementById(id);
      if (!container) return;
      const pickers = container.querySelectorAll('.ql-picker');
      pickers.forEach((el: any) => {
        if (el && el.style && el.style.display === 'none')
          el.style.display = '';
      });
      const labels = container.querySelectorAll('.ql-picker-label');
      labels.forEach((label: any) => {
        if (label && label.getAttribute)
          label.setAttribute('aria-expanded', 'false');
      });
    } catch (_) {}
  }, [id]);

  useEffect(() => {
    // HTML → 일반 전환 직후 한 프레임 뒤 초기화
    if (!isHtmlMode) {
      const raf = requestAnimationFrame(() => resetPickerDisplay());
      return () => cancelAnimationFrame(raf);
    }
  }, [isHtmlMode, resetPickerDisplay]);

  // 중복 토글 방지(더블 클릭/이중 이벤트)
  const toggleLockRef = useRef(false);
  const handleToggleHtmlClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (toggleLockRef.current) return;
      toggleLockRef.current = true;
      setTimeout(() => (toggleLockRef.current = false), 300);

      if (onToggleHtmlMode) {
        onToggleHtmlMode();
        return;
      }

      if (quillRef?.current) {
        try {
          const editor = quillRef.current.getEditor();
          const toolbar = editor.getModule('toolbar');
          if (toolbar?.handlers?.toggleHTML) {
            toolbar.handlers.toggleHTML();
          }
        } catch (error) {
          // noop
        }
      }
    },
    [onToggleHtmlMode, quillRef],
  );

  // 🆕 이전 toolbar DOM 정리를 위한 ref
  const prevToolbarRef = useRef<string | null>(null);

  // 🆕 컴포넌트 마운트 시 이전 toolbar DOM 정리
  useEffect(() => {
    const currentToolbarId = id;

    // 이전 toolbar와 다른 ID인 경우 이전 것을 정리
    if (prevToolbarRef.current && prevToolbarRef.current !== currentToolbarId) {
      const prevToolbar = document.getElementById(prevToolbarRef.current);
      if (prevToolbar) {
        // 🆕 이전 toolbar의 모든 자식 요소 제거
        const quillElements = prevToolbar.querySelectorAll(
          '.ql-size, .ql-header, .ql-align, .ql-background, .ql-color',
        );
        quillElements.forEach((el) => el.remove());
        console.log('🧹 이전 toolbar DOM 정리:', prevToolbarRef.current);
      }
    }

    prevToolbarRef.current = currentToolbarId;

    return () => {
      // 🆕 컴포넌트 언마운트 시에도 정리
      const toolbarContainer = document.getElementById(currentToolbarId);
      if (toolbarContainer) {
        const quillElements = toolbarContainer.querySelectorAll(
          '.ql-size, .ql-header, .ql-align, .ql-background, .ql-color',
        );
        quillElements.forEach((el) => el.remove());
      }
    };
  }, [id]);

  if (!setToolbar) return null;
  return (
    <>
      <ToolbarWrap
        transparent={transparent}
        style={{
          justifyContent: toolbarSide ? 'space-between' : 'center',
          visibility: noToolbar ? 'hidden' : 'visible',
          ...style,
        }}
      >
        <ToolbarContainer
          id={id}
          key={id}
          dir={dir}
          className="ql-toolbar"
          data-rows={rows}
          style={{
            flexWrap: rows === 2 ? 'wrap' : 'nowrap',
            rowGap: rows === 2 ? 4 : 0,
          }}
        >
          {/* 단일 구조 유지: 스타일로 2단 구성 */}
          <ToolbarSec1 setToolbar={setToolbar} quillRef={quillRef} />
          {color && (
            <>
              <ToolbarButton
                className="ql-background"
                data-tooltip="폰트 배경"
                style={{ position: 'relative' }}
                onClick={() => {
                  setBgPickerVisible(!isBgPickerVisible);
                  setPickerVisible(false);
                }}
              />

              <ToolbarButton
                className="ql-color"
                data-tooltip="폰트 컬러"
                style={{ position: 'relative' }}
                onClick={() => {
                  setPickerVisible(!isPickerVisible);
                  setBgPickerVisible(false);
                }}
              />
            </>
          )}
          <ToolbarSec2 setToolbar={setToolbar} quillRef={quillRef} />
          {/* 2단 옵션일 때 여기서 줄바꿈 */}
          <div style={{ flexBasis: rows === 2 ? '100%' : 'auto', height: 0 }} />
          <ToolbarSec3
            setToolbar={setToolbar}
            quillRef={quillRef}
            emojiDirection={emojiDirection}
            collectionPath={collectionPath}
            setVideoURLList={setVideoURLList}
            videoDropdownDirection={videoDropdownDirection}
          />
          {(size || align || color || list || header) && (
            <ToolbarButton className="ql-clean" data-tooltip="스타일 리셋" />
          )}
          {html && (
            <ToolbarButton
              className="ql-toggleHTML"
              data-tooltip="HTML모드 켜기"
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={handleToggleHtmlClick}
            >
              {isHtmlMode ? 'HTML모드 해제' : 'HTML'}
            </ToolbarButton>
          )}
        </ToolbarContainer>
        {toolbarSide}
        {/* HTML 모드와 무관하게 컬러피커 렌더링 */}
        {quill && (
          <EditorColorPicker isVisible={isPickerVisible} quill={quill} />
        )}
        {quill && (
          <EditorBgColorPicker isVisible={isBgPickerVisible} quill={quill} />
        )}
      </ToolbarWrap>
    </>
  );
}

export default React.memo(CustomToolBar);
